import { MODEL_DATA_VOLUME_BUFFER_GI } from './constants';

/** K8s 二进制单位 -> Gi 数值 */
const BINARY_SUFFIX_TO_GI: Record<string, number> = {
  Ki: 1 / 1024 / 1024,
  Mi: 1 / 1024,
  Gi: 1,
  Ti: 1024,
  Pi: 1024 * 1024,
  Ei: 1024 * 1024 * 1024,
};

/** K8s 十进制单位 -> Gi 数值 */
const DECIMAL_SUFFIX_TO_GI: Record<string, number> = {
  m: 1e-3 / 1024 ** 3,
  k: 1e3 / 1024 ** 3,
  M: 1e6 / 1024 ** 3,
  G: 1e9 / 1024 ** 3,
  T: 1e12 / 1024 ** 3,
  P: 1e15 / 1024 ** 3,
  E: 1e18 / 1024 ** 3,
};

/** 将任意 K8s quantity 或表单数值解析为 Gi（纯数字按 Gi 理解） */
function parseQuantityCore(raw: string): number | undefined {
  if (/^[+-]?\d+(\.\d+)?$/.test(raw)) {
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : undefined;
  }

  const match = raw.match(/^([+-]?(?:\d+\.?\d*|\.\d+))([A-Za-z]+)$/);
  if (!match) return undefined;

  const num = Number(match[1]);
  if (!Number.isFinite(num) || num <= 0) return undefined;

  const suffix = match[2];
  if (suffix in BINARY_SUFFIX_TO_GI) {
    return num * BINARY_SUFFIX_TO_GI[suffix];
  }
  if (suffix in DECIMAL_SUFFIX_TO_GI) {
    return num * DECIMAL_SUFFIX_TO_GI[suffix];
  }
  return undefined;
}

function parseQuantityToGiNumber(value: string): number | undefined {
  const raw = value.trim();
  if (!raw) return undefined;

  // 修复错误拼接 Gi 后缀（如 8192MiGi、8GiGi）
  if (/Gi$/i.test(raw) && raw.length > 2) {
    const innerGi = parseQuantityCore(raw.slice(0, -2));
    if (innerGi !== undefined) return innerGi;
  }

  return parseQuantityCore(raw);
}

/** 格式化为 K8s 可解析的 Gi quantity 字符串 */
function formatGiQuantity(gi: number): string {
  const rounded = Math.round(gi * 1000) / 1000;
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return `${text}Gi`;
}

/** 将存储 quantity 解析为 Gi 数值（如 100Gi、8192Mi） */
export function parseStorageQuantityToGi(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return parseQuantityToGiNumber(String(value));
}

/** 字节转 Gi */
export function bytesToGi(bytes: number): number {
  return bytes / 1024 ** 3;
}

/** 创建数据盘时卷容量下限（Gi）：模型大小 + 预留空间 */
export function getMinCreateDataVolumeStorageGi(modelSizeGi?: number): number {
  if (modelSizeGi === undefined || modelSizeGi <= 0) return 1;
  return modelSizeGi + MODEL_DATA_VOLUME_BUFFER_GI;
}

/** 根据模型字节数计算创建数据盘时的卷容量下限（Gi） */
export function getMinCreateDataVolumeStorageGiFromBytes(modelSizeBytes?: number): number {
  if (!modelSizeBytes || modelSizeBytes <= 0) return 1;
  return getMinCreateDataVolumeStorageGi(Math.ceil(bytesToGi(modelSizeBytes)));
}

/** 新建卷容量是否不足以容纳模型（需满足：卷容量 >= 模型大小 + 5Gi） */
export function isCreateDataVolumeStorageInsufficient(modelSizeGi: number, storageGi: number): boolean {
  return modelSizeGi + MODEL_DATA_VOLUME_BUFFER_GI > storageGi;
}

/** 将 Gi 数值字符串规范化，兼容 Mi/Ti/G 等单位及纯数字 */
export function toK8sGiQuantity(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const gi = parseQuantityToGiNumber(String(value));
  if (gi === undefined || gi <= 0) return undefined;
  return formatGiQuantity(gi);
}
